import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Clapperboard, Search, Film, Tv, Link2, Pencil, Trash2, Copy, ChevronLeft, ChevronRight, Users, Play, Sparkles } from "lucide-react";
import { api, formatApiError, fmtBytes } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { GlassCard, PageHeader, Spinner, Modal } from "../components/common";
import { DomainGate } from "../components/DomainGate";

function CatalogCard({ item, creds, onPlay, onCopy, onPoster, onEdit, onDelete, busy }) {
  const [imgFail, setImgFail] = useState(false);
  const isSeries = item.media_type === "tv";
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/50 overflow-hidden flex flex-col group" data-testid={`catalog-card-${item.id}`}>
      <div className="aspect-[2/3] bg-slate-800/60 relative overflow-hidden">
        {item.poster_url && !imgFail ? (
          <img src={item.poster_url} alt={item.title} loading="lazy" onError={() => setImgFail(true)}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" data-testid={`catalog-poster-${item.id}`} />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-slate-600">
            {isSeries ? <Tv size={26} /> : <Film size={26} />}
            <span className="text-[9px] font-mono2 uppercase tracking-widest">sem capa</span>
          </div>
        )}
        <span className={`absolute top-1.5 left-1.5 text-[8px] font-mono2 uppercase tracking-widest px-1.5 py-0.5 rounded ${isSeries ? "bg-amber-500/80 text-white" : "bg-cyan-500/80 text-slate-950"}`}>
          {isSeries ? "Série" : "Filme"}
        </span>
        {creds && (
          <button type="button" data-testid={`catalog-play-${item.id}`} title="Teste de 10s — reproduz sem sair da página" onClick={() => onPlay(item)}
            className="absolute inset-0 m-auto w-11 h-11 rounded-full bg-black/60 border border-cyan-400/50 text-cyan-300 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-cyan-400 hover:text-slate-950">
            <Play size={18} className="ml-0.5" />
          </button>
        )}
      </div>
      <div className="p-2.5 space-y-1 flex-1 flex flex-col">
        <p className="text-[11px] text-slate-200 font-medium leading-tight line-clamp-2" title={item.title}>
          {item.title}{item.year ? ` (${item.year})` : ""}
        </p>
        <p className="text-[9px] font-mono2 text-slate-500 truncate">{item.category || "(Sem pasta)"}</p>
        <p className="text-[9px] font-mono2 text-slate-600">{item.versions} {isSeries ? "episódio(s)" : "versão(ões)"} · {fmtBytes(item.size_bytes || 0)}</p>
        <div className="flex gap-1 pt-1 mt-auto">
          <button type="button" data-testid={`catalog-copy-${item.id}`} title={creds ? "Copiar link de reprodução" : "Crie um acesso na aba Acessos para copiar links"}
            onClick={() => onCopy(item)} disabled={!creds}
            className="flex-1 text-[10px] px-1.5 py-1.5 rounded-lg btn-ghost-dark text-slate-400 hover:text-cyan-300 disabled:opacity-30 flex items-center justify-center">
            <Link2 size={11} />
          </button>
          <button type="button" data-testid={`catalog-poster-gen-${item.id}`} title="Gerar capa (TMDB inteligente — aplica à série toda)" onClick={() => onPoster(item)} disabled={busy === item.id}
            className="flex-1 text-[10px] px-1.5 py-1.5 rounded-lg btn-ghost-dark text-slate-400 hover:text-amber-300 disabled:opacity-40 flex items-center justify-center">
            {busy === item.id ? <Spinner /> : <Sparkles size={11} />}
          </button>
          <button type="button" data-testid={`catalog-edit-${item.id}`} title="Editar / renomear" onClick={() => onEdit(item)}
            className="flex-1 text-[10px] px-1.5 py-1.5 rounded-lg btn-ghost-dark text-slate-400 hover:text-amber-300 flex items-center justify-center">
            <Pencil size={11} />
          </button>
          <button type="button" data-testid={`catalog-delete-${item.id}`} title="Excluir (apaga os ficheiros da nuvem)" onClick={() => onDelete(item)}
            className="flex-1 text-[10px] px-1.5 py-1.5 rounded-lg btn-ghost-dark text-slate-400 hover:text-rose-300 flex items-center justify-center">
            <Trash2 size={11} />
          </button>
        </div>
      </div>
    </div>
  );
}

function PlayerBox({ src }) {
  const [done, setDone] = useState(false);
  const [err, setErr] = useState(false);
  return (
    <div className="space-y-3">
      <video src={src} controls autoPlay playsInline className="w-full rounded-xl bg-black aspect-video" data-testid="catalogo-player-video"
        onTimeUpdate={(e) => { if (e.target.currentTime >= 10 && !done) { e.target.pause(); setDone(true); toast.success("Teste de 10s concluído — o stream está a funcionar"); } }}
        onError={() => setErr(true)} />
      {done && <p className="text-xs text-emerald-300 bg-emerald-400/10 border border-emerald-400/25 rounded-xl px-4 py-2.5" data-testid="catalogo-player-ok">Teste concluído: os primeiros 10 segundos reproduziram sem erros.</p>}
      {err && <p className="text-xs text-rose-300 bg-rose-400/10 border border-rose-400/25 rounded-xl px-4 py-2.5" data-testid="catalogo-player-err">O browser não conseguiu abrir o stream (codec não suportado ou origem lenta). Copie o link e tente no VLC.</p>}
      <p className="text-[10px] text-slate-600 text-center">Teste limitado a 10 segundos — consome 1 ligação do acesso enquanto toca.</p>
    </div>
  );
}

export default function Catalogo() {
  const { user } = useAuth();
  const isAdmin = user.role === "super_admin";
  const [kind, setKind] = useState("movie");
  const [data, setData] = useState(null);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState("");
  const [accountId, setAccountId] = useState("");
  const [accounts, setAccounts] = useState(null);
  const [edit, setEdit] = useState(null); // item em edição
  const [editForm, setEditForm] = useState({ title: "", year: "", poster_url: "" });
  const [del, setDel] = useState(null);
  const [eps, setEps] = useState(null); // {title, creds, versions} — modal de episódios p/ copiar link
  const [player, setPlayer] = useState(null); // {title, src} — teste de 10s embutido

  const qs = useCallback((extra = "") => `${extra ? extra + "&" : "?"}${accountId ? `user_id=${accountId}` : ""}`.replace(/[?&]$/, ""), [accountId]);

  const load = useCallback(async (p = page) => {
    try {
      const { data: d } = await api.get(`/folders/catalog${qs(`?kind=${kind}&q=${encodeURIComponent(query)}&page=${p}`)}`);
      setData(d);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [qs, kind, query, page]);

  useEffect(() => { load(); }, [load]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); load(1); }, 300);
    return () => clearTimeout(t);
  }, [query, kind, accountId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isAdmin) {
      api.get("/folders/tree").then((r) => setAccounts(r.data.accounts || [])).catch(() => {});
    }
  }, [isAdmin]);

  const origin = window.location.origin;
  const mkLink = (k, vid) => `${origin}/api/xc/${k}/${data.creds.username}/${data.creds.password}/${vid}.mp4`;

  const copyItem = async (item) => {
    if (item.media_type === "tv") {
      try {
        const { data: d } = await api.get(`/folders/title/${item.id}/detail${qs()}`);
        setEps(d);
      } catch (e) { toast.error(formatApiError(e)); }
      return;
    }
    if (!item.best_version_id) { toast.error("Este filme não tem ficheiro"); return; }
    navigator.clipboard.writeText(mkLink("movie", item.best_version_id));
    toast.success("Link copiado — cole no VLC/player");
  };

  const playItem = (item) => {
    if (!data?.creds) return;
    if (item.media_type === "tv") { copyItem(item); return; } // série: abre o modal de episódios (cada um tem ▶)
    if (!item.best_version_id) { toast.error("Este filme não tem ficheiro"); return; }
    setPlayer({ title: item.title, src: mkLink("movie", item.best_version_id) });
  };

  const genPoster = async (item) => {
    setBusy(item.id);
    try {
      await api.post(`/posters/generate/${item.id}${qs()}`);
      toast.success("Capa gerada — aplicada a toda a série");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const saveEdit = async () => {
    if (!edit) return;
    setBusy("edit");
    try {
      const payload = { title: editForm.title.trim() };
      if (editForm.year) payload.year = parseInt(editForm.year, 10);
      if (editForm.poster_url.trim()) payload.poster_url = editForm.poster_url.trim();
      await api.patch(`/folders/items/${edit.id}${qs()}`, payload);
      toast.success("Título atualizado");
      setEdit(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const confirmDelete = async () => {
    if (!del) return;
    setBusy("del");
    try {
      await api.delete(`/folders/items/${del.id}${qs()}`);
      toast.success(`"${del.title}" excluído — ficheiros apagados da nuvem e cota libertada`);
      setDel(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.per_page)) : 1;

  return (
    <div className="space-y-6" data-testid="catalogo-page">
      <PageHeader eyebrow="Acervo" title="Catálogo" subtitle="Todos os filmes e séries em grelha, divididos por tipo — copie links, edite, renomeie ou exclua qualquer título." />
      <DomainGate />

      <div className="grid grid-cols-2 gap-4" data-testid="catalogo-kind-cards">
        {[
          { id: "movie", label: "Filmes", Icon: Film, cls: "cyan" },
          { id: "tv", label: "Séries", Icon: Tv, cls: "violet" },
        ].map(({ id, label, Icon, cls }) => {
          const active = kind === id;
          return (
            <button key={id} type="button" data-testid={`catalogo-kind-${id}`} onClick={() => { setKind(id); setPage(1); }}
              className={`glass rounded-2xl p-4 text-left transition-all flex items-center gap-3 ${active
                ? cls === "violet" ? "border-amber-400/50 bg-amber-400/5" : "border-cyan-400/50 bg-cyan-400/5"
                : "hover:border-slate-600"}`}>
              <div className={`w-9 h-9 rounded-xl border flex items-center justify-center shrink-0 ${cls === "violet" ? "bg-amber-400/10 border-amber-400/25" : "bg-cyan-400/10 border-cyan-400/25"}`}>
                <Icon size={16} className={cls === "violet" ? "text-amber-300" : "text-cyan-300"} />
              </div>
              <p className="font-display text-white font-bold text-sm">{label}</p>
              <span className={`ml-auto w-2.5 h-2.5 rounded-full shrink-0 transition-colors ${active ? (cls === "violet" ? "bg-amber-400" : "bg-cyan-400") : "bg-slate-700"}`} />
            </button>
          );
        })}
      </div>

      <GlassCard className="p-4 space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input data-testid="catalogo-search-input" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder={`Pesquisar ${kind === "tv" ? "séries" : "filmes"}…`} className="input-dark w-full rounded-lg pl-9 pr-3 py-2 text-sm" />
          </div>
          {isAdmin && accounts && (
            <div className="flex items-center gap-2">
              <Users size={14} className="text-amber-400" />
              <select data-testid="catalogo-account-select" value={accountId} onChange={(e) => { setAccountId(e.target.value); setPage(1); }} className="input-dark rounded-lg px-3 py-2 text-sm max-w-[200px]">
                <option value="">Minha conta</option>
                {accounts.map((a) => <option key={a.id} value={a.id}>{a.name || a.email}</option>)}
              </select>
            </div>
          )}
          {data && <span className="text-[11px] font-mono2 text-slate-500" data-testid="catalogo-total">{data.total} título(s)</span>}
        </div>

        {!data ? (
          <div className="flex justify-center py-12"><Spinner /></div>
        ) : data.items.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-12" data-testid="catalogo-empty">
            Nenhum {kind === "tv" ? "série" : "filme"} {query ? `para "${query}"` : "com ficheiros"} no acervo.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="catalogo-grid">
              {data.items.map((it) => (
                <CatalogCard key={it.id} item={it} creds={data.creds} busy={busy}
                  onPlay={playItem}
                  onCopy={copyItem}
                  onPoster={genPoster}
                  onEdit={(i) => { setEdit(i); setEditForm({ title: i.title, year: i.year || "", poster_url: i.poster_url || "" }); }}
                  onDelete={setDel} />
              ))}
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3 pt-2" data-testid="catalogo-pagination">
                <button data-testid="catalogo-prev-page" onClick={() => { const p = page - 1; setPage(p); load(p); }} disabled={page <= 1} className="p-2 rounded-lg btn-ghost-dark text-slate-400 disabled:opacity-30"><ChevronLeft size={14} /></button>
                <span className="text-[11px] font-mono2 text-slate-500">{page} / {totalPages}</span>
                <button data-testid="catalogo-next-page" onClick={() => { const p = page + 1; setPage(p); load(p); }} disabled={page >= totalPages} className="p-2 rounded-lg btn-ghost-dark text-slate-400 disabled:opacity-30"><ChevronRight size={14} /></button>
              </div>
            )}
          </>
        )}
        {data && !data.creds && (
          <p className="text-[11px] text-amber-300/80" data-testid="catalogo-nocreds">Para copiar links, crie antes um acesso na aba Acessos.</p>
        )}
      </GlassCard>

      <Modal open={!!edit} onClose={() => setEdit(null)} title={`Editar — ${edit?.title || ""}`} testid="catalogo-edit-modal">
        <div className="space-y-3">
          <div>
            <label className="text-[10px] font-mono2 uppercase tracking-widest text-slate-500">Título (renomear)</label>
            <input data-testid="catalogo-edit-title" value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} className="input-dark w-full rounded-lg px-3 py-2.5 text-sm mt-1" />
          </div>
          <div>
            <label className="text-[10px] font-mono2 uppercase tracking-widest text-slate-500">Ano</label>
            <input data-testid="catalogo-edit-year" value={editForm.year} onChange={(e) => setEditForm({ ...editForm, year: e.target.value.replace(/\D/g, "").slice(0, 4) })} placeholder="2024" className="input-dark w-full rounded-lg px-3 py-2.5 text-sm mt-1" />
          </div>
          <div>
            <label className="text-[10px] font-mono2 uppercase tracking-widest text-slate-500">Capa (URL de imagem)</label>
            <div className="flex gap-2 mt-1">
              <input data-testid="catalogo-edit-poster" value={editForm.poster_url} onChange={(e) => setEditForm({ ...editForm, poster_url: e.target.value })} placeholder="https://…" className="input-dark flex-1 rounded-lg px-3 py-2.5 text-sm font-mono2" />
              <button type="button" data-testid="catalogo-edit-auto-poster" disabled={busy === "poster"} title="Gerar capa automaticamente (lista → acervo → TMDB)"
                onClick={async () => {
                  setBusy("poster");
                  try {
                    const { data } = await api.post(`/posters/generate/${edit.id}${qs()}`);
                    if (data.poster_url) { setEditForm({ ...editForm, poster_url: data.poster_url }); toast.success("Capa gerada automaticamente"); }
                    else toast.error("Não encontrei capa para este título");
                  } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
                }}
                className="btn-ghost-dark px-3 py-2.5 rounded-lg text-[11px] text-amber-300 flex items-center gap-1.5 shrink-0 disabled:opacity-40">
                {busy === "poster" ? <Spinner /> : <><Sparkles size={13} /> Automática</>}
              </button>
            </div>
          </div>
          {editForm.poster_url.trim().startsWith("http") && (
            <div className="flex justify-center"><img src={editForm.poster_url.trim()} alt="pré-visualização" className="max-h-40 rounded-lg border border-slate-800" data-testid="catalogo-edit-preview" /></div>
          )}
          <button data-testid="catalogo-edit-save" onClick={saveEdit} disabled={!editForm.title.trim() || !!busy} className="btn-neon w-full py-3 rounded-xl text-sm flex items-center justify-center gap-2">
            {busy === "edit" ? <Spinner /> : <><Pencil size={14} /> Guardar alterações</>}
          </button>
        </div>
      </Modal>

      <Modal open={!!del} onClose={() => setDel(null)} title="Excluir título" testid="catalogo-delete-modal">
        <div className="space-y-3">
          <p className="text-sm text-slate-300">Excluir <b className="text-white">{del?.title}</b>? Todas as versões/episódios são apagados da nuvem e a cota é libertada. Esta ação é irreversível.</p>
          <button data-testid="catalogo-delete-confirm" onClick={confirmDelete} disabled={!!busy} className="w-full py-3 rounded-xl text-sm bg-rose-500/15 border border-rose-400/40 text-rose-300 hover:bg-rose-500/25 transition-colors flex items-center justify-center gap-2">
            {busy === "del" ? <Spinner /> : <><Trash2 size={14} /> Excluir definitivamente</>}
          </button>
        </div>
      </Modal>

      <Modal open={!!player} onClose={() => setPlayer(null)} title={`Teste de 10s — ${player?.title || ""}`} testid="catalogo-player-modal">
        {player && <PlayerBox src={player.src} />}
      </Modal>

      <Modal open={!!eps} onClose={() => setEps(null)} title={`Episódios — ${eps?.title || ""}`} testid="catalogo-eps-modal">
        <div className="space-y-1.5 max-h-[50vh] overflow-y-auto pr-1">
          {(eps?.versions || []).map((v) => {
            const label = v.season != null ? `S${String(v.season).padStart(2, "0")}E${String(v.episode || 0).padStart(2, "0")}` : (v.version || "").toUpperCase();
            const link = eps.creds ? mkLink("series", v.id) : null;
            return (
              <div key={v.id} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-800/60" data-testid={`catalogo-ep-${v.id}`}>
                <span className="text-[11px] font-mono2 text-cyan-300 w-16 shrink-0">{label}</span>
                {v.quality && <span className="text-[9px] font-mono2 uppercase px-1.5 py-0.5 rounded bg-slate-800 text-slate-400">{v.quality}</span>}
                <span className="text-[10px] font-mono2 text-slate-500">{fmtBytes(v.size_bytes || 0)}</span>
                {link && (
                  <button type="button" data-testid={`catalogo-ep-play-${v.id}`} title="Teste de 10s" onClick={() => setPlayer({ title: `${eps.title} ${label}`, src: link })}
                    className="ml-auto p-1.5 rounded-lg text-slate-500 hover:text-emerald-300 hover:bg-emerald-400/10 transition-colors"><Play size={13} /></button>
                )}
                {link ? (
                  <button type="button" data-testid={`catalogo-ep-copy-${v.id}`} onClick={() => { navigator.clipboard.writeText(link); toast.success("Link do episódio copiado"); }}
                    className={`${link ? "" : "ml-auto "}p-1.5 rounded-lg text-slate-500 hover:text-cyan-300 hover:bg-cyan-400/10 transition-colors`}><Copy size={13} /></button>
                ) : <span className="ml-auto text-[9px] font-mono2 text-slate-600">sem acesso</span>}
              </div>
            );
          })}
        </div>
      </Modal>
    </div>
  );
}
